/**
 * AI menu import contracts. Mirror of the API declarations in
 * `src/menu-imports/engine/draft.types.ts` (draft model, plan §5.3) and
 * `src/menu-imports/menu-import.contracts.ts` (snapshot, deltas and events,
 * plan §5.4), plus the draft operations of `engine/ops.ts` (§8.11) and the
 * request bodies of the `/menu-imports` endpoints (§8.3). Keep both sides equal.
 */
import type { ComandaSplit } from '@/enums/ComandaSplit';
import type { ISimpleHourRange } from '@/interfaces/ISimpleHourRange';

// ---------------------------------------------------------------------------
// Draft model (§5.3)
// ---------------------------------------------------------------------------

export type TDraftOrigin = 'ai' | 'copy' | 'manual';
export type TReviewLevel = 'ok' | 'check' | 'fix';
export type TItemFlag =
  /** Update sessions (derived): the photo price is more than 2.5 × today's. */
  | 'price_jump'
  /** …the photo price is lower than today's. */
  | 'price_down'
  /** …the sizes of the photo and of the live dish do not fit together. */
  | 'sizes_differ'
  /** …the live dish has a promotional price and the photo price differs. */
  | 'price_promo'
  | 'name_unclear'
  | 'price_unclear'
  | 'sizes_unclear'
  | 'addons_unclear'
  | 'cut_off'
  | 'handwritten'
  | 'inferred_category'
  | 'possible_duplicate'
  | 'price_outlier'
  | 'generated_text'
  | 'combo'
  | 'foreign_currency'
  /** The price is printed before tax («+ IVA»): diners must see the final price. */
  | 'price_pre_tax';
export type TReviewReason =
  | TItemFlag
  | 'missing_price'
  | 'question'
  | 'allergens'
  | 'addons'
  /** A new dish of an update session that may be a live dish (`live.candidateIds`). */
  | 'live_suggested';

/** Model-facing allergen codes = the admin's `allergens.*` i18n keys; bit = src/enums/Allergens.ts. */
export const ALLERGEN_BITS = {
  lupins: 1,
  celery: 2,
  peanuts: 4,
  crustaceans: 8,
  nuts: 16,
  gluten: 32,
  eggs: 64,
  dairy: 128,
  mollusks: 256,
  mustard: 512,
  fish: 1024,
  sesame: 2048,
  soy: 4096,
  sulphites: 8192,
} as const;
export type TAllergenKey = keyof typeof ALLERGEN_BITS;

export interface IDraftSize {
  id: string;
  label: string;
  price: number | null;
  priceRaw: string | null;
}

/** box = [ymin, xmin, ymax, xmax] in 0..1000 of the page image. */
export interface IDraftSourceRef {
  pageId: string;
  box: [number, number, number, number] | null;
}

/** Non-draft item fields that travel with a copied dish (API `catalog-tree.types.ts`). */
export interface IItemCarry {
  stringId: string | null;
  recipe: string | null;
  vat: number | null;
  discount: number | null;
  discountMode: number | null;
  discountedPrice: number | null;
  hourRanges: ISimpleHourRange[] | null;
  comandaPrinters: number[] | null;
  comandaSplit: ComandaSplit;
  labelTemplate: string | null;
}

/** Non-draft category fields that travel with a copied category. */
export interface ICategoryCarry {
  picture: string | null;
  comandaPrinters: number[] | null;
  hourRanges: ISimpleHourRange[] | null;
  posOnly: boolean;
}

/** Non-draft add-on group fields that travel with a copied group. */
export interface IGroupCarry {
  description: string | null;
  active: boolean;
}

export interface IDraftItemReview {
  level: TReviewLevel;
  reason: TReviewReason | null;
}

export interface IDraftItem {
  /** 'i17' */
  id: string;
  categoryId: string;
  /** 1..128 */
  name: string;
  /** ≤1000 */
  description: string | null;
  /** > 0, 2 decimals; null = missing (or the dish has sizes). */
  price: number | null;
  /** As printed. */
  priceRaw: string | null;
  /** «Consultar», «Según mercado». */
  priceNote: string | null;
  sizesTitle: string | null;
  /** [] or 2..20 (a copied dish may carry exactly the sizes it had). */
  sizes: IDraftSize[];
  /** Ordered. */
  addonGroupIds: string[];
  /** Bitmask of ALLERGEN_BITS. */
  allergens: number;
  allergensConfirmed: boolean;
  /** 'temp/…' (owner upload) or a final key (copy). */
  photo: string | null;
  hidden: boolean;
  sourceRefs: IDraftSourceRef[];
  flags: TItemFlag[];
  /** Computed by the server after every mutation (§8.11). */
  review: IDraftItemReview;
  origin: TDraftOrigin;
  /** True once the owner changed it; the reader never touches edited dishes. */
  edited: boolean;
  readNo: number | null;
  /** readNo·1e6 + page position·1e3 + ordinal for AI dishes (menu order across pages). */
  sortKey: number;
  /** Copies only. */
  carry: IItemCarry | null;
  /** Update sessions: the dish's link to the live menu (absent otherwise). */
  live?: IDraftItemLive;
}

export interface IDraftCategory {
  id: string;
  name: string;
  description: string | null;
  hidden: boolean;
  itemIds: string[];
  origin: TDraftOrigin;
  sortKey: number;
  carry: ICategoryCarry | null;
  /** Update sessions: the live category its new dishes go into (its name is never published). */
  liveCategoryId?: number;
}

// ---------------------------------------------------------------------------
// Live update (menu update from photos)
// ---------------------------------------------------------------------------

/** A choice of a live dish's main option (its size). */
export interface ILiveSize {
  optionItemId: number;
  label: string;
  price: number;
  discountedCost: number | null;
  active: boolean;
}

/** A live dish of the store (hidden ones included), with its sizes. */
export interface ILiveDish {
  itemId: number;
  name: string;
  categoryId: number;
  visible: boolean;
  price: number;
  discountedPrice: number | null;
  /** The main option of `sizes`; null without sizes. */
  mainOptionId: number | null;
  /** ≥ 2 sizes, or exactly 1 for a single choice, or []. */
  sizes: ILiveSize[];
}

export interface ILiveCategory {
  id: number;
  name: string;
}

/** A live dish the owner can pick or hide (no sizes). */
export interface ILiveOther {
  itemId: number;
  name: string;
  categoryId: number;
  visible: boolean;
  /** Today's price. */
  price: number;
  /** Its live category was photographed. */
  photographed: boolean;
}

export type TLiveChange =
  | { kind: 'price'; from: number; to: number }
  | {
      kind: 'size';
      optionItemId: number;
      label: string;
      from: number;
      to: number;
    }
  | { kind: 'newSize'; label: string; to: number };

export interface IDraftItemLive {
  /** Who decided the link: the matcher or the owner. */
  by: 'rules' | 'owner';
  /** The live dish this dish updates; null = published as new. */
  dish: ILiveDish | null;
  /** Rules only, `dish` null: 1..3 live dishes it may be (ids of `draft.live.others`). */
  candidateIds: number[];
  /** Server-computed: what publishing changes on `dish`. */
  changes: TLiveChange[];
  /** The changes the owner accepted (holds are lifted while they are the current ones). */
  accepted?: string;
  /** For each draft size, the paired live `optionItemId` (null when new or unpaired). */
  sizeIds?: (number | null)[];
}

export interface IDraftLive {
  categories: ILiveCategory[];
  /** Live dishes no draft dish is linked to, store order. */
  others: ILiveOther[];
  /** Others the owner chose to hide on publish (insertion order). */
  hiddenIds: number[];
}

/** price ≥ 0 */
export interface IDraftAddonOption {
  id: string;
  name: string;
  price: number;
}

export type TAddonGroupKind = 'choose' | 'add';

export interface IDraftAddonGroup {
  id: string;
  title: string;
  kind: TAddonGroupKind;
  required: boolean;
  multiple: boolean;
  min: number | null;
  max: number | null;
  options: IDraftAddonOption[];
  origin: TDraftOrigin;
  confirmed: boolean;
  carry: IGroupCarry | null;
}

export type TQuestionKind =
  | 'price_format'
  | 'currency'
  | 'variant_merge'
  | 'addon_scope'
  | 'size_columns'
  | 'combo'
  | 'category'
  | 'duplicate'
  | 'payment_prices'
  /** Update sessions: most read dishes are new and most live dishes are missing. */
  | 'new_menu';

/** One of the two prices of a dish printed per payment method. */
export interface IPaymentPrice {
  label: string;
  price: number;
  priceRaw: string | null;
}

/** A dish the read gave its higher payment-method price (`kept`), the other one in its note. */
export interface IPaymentPricePair {
  itemId: string;
  kept: IPaymentPrice;
  other: IPaymentPrice;
  /** Ends the description while the higher price is shown («Efectivo: 15 % menos»); empty when almost equal. */
  higherNote: string;
  /** Replaces it when the lower price is shown («Efectivo»). */
  lowerNote: string;
}

/** Blocking kinds: `price_format`, `currency`, `payment_prices`. */
export interface IDraftQuestionBase {
  id: string;
  itemIds: string[];
  groupId: string | null;
  blocking: boolean;
  status: 'open' | 'answered';
  answer: string | null;
  readNo: number | null;
}

export type TDraftQuestion = IDraftQuestionBase &
  (
    | { kind: 'price_format'; params: { priceRaw: string; example: number } }
    | {
        kind: 'currency';
        params: { detected: string; mark: string; store: string };
      }
    | { kind: 'variant_merge'; params: { names: string[] } }
    | {
        kind: 'addon_scope';
        params: {
          groupTitle: string;
          options: { name: string; price: number }[];
          categoryName: string;
        };
      }
    | {
        kind: 'size_columns';
        params: { columns: string[]; categoryName: string };
      }
    | { kind: 'combo'; params: { name: string } }
    | { kind: 'category'; params: { name: string } }
    | { kind: 'duplicate'; params: { name: string; prices: (number | null)[] } }
    | { kind: 'payment_prices'; params: { pairs: IPaymentPricePair[] } }
    | { kind: 'new_menu'; params: { count: number } }
  );

/**
 * Answer values by kind: `price_format` 'yes' | 'no'; `currency` 'detected' |
 * 'store'; `variant_merge` 'merge' | 'keep'; `addon_scope` 'all' | 'some'
 * (+ itemIds); `size_columns` 'yes' | 'no'; `combo` 'keep' | 'split';
 * `category` <categoryId> | 'new' (+ categoryName); `duplicate` 'keep_one' |
 * 'keep_both'; `payment_prices` 'higher' | 'lower'; `new_menu` 'hide' | 'keep'.
 */
export type TQuestionAnswer = string;

export interface IDraftCopyPrintStation {
  stationId: number;
  name: string;
  index: number;
}

export interface IDraftCopyMeta {
  sourceRestaurantId: number;
  sourceName: string;
  printStations: IDraftCopyPrintStation[];
}

/** Id counters, never reused (`c3`, `i17`, `s4`, `g2`, `o9`, `q1`). */
export interface IDraftSeq {
  c: number;
  i: number;
  s: number;
  g: number;
  o: number;
  q: number;
}

export interface IMenuDraft {
  schema: 1;
  /** Detected main language of the dish names. */
  languageId: string | null;
  /** Set only by the `currency` answer. */
  currency: string | null;
  seq: IDraftSeq;
  categoryOrder: string[];
  categories: Record<string, IDraftCategory>;
  items: Record<string, IDraftItem>;
  addonGroups: Record<string, IDraftAddonGroup>;
  questions: Record<string, TDraftQuestion>;
  questionOrder: string[];
  /** Copy sessions only. */
  copy: IDraftCopyMeta | null;
  /** Update sessions (the store had dishes when the draft was matched); absent otherwise. */
  live?: IDraftLive;
}

// ---------------------------------------------------------------------------
// Draft operations (§8.11)
// ---------------------------------------------------------------------------

export interface IDraftSizeInput {
  label: string;
  price: number | null;
}

export interface IDraftAddonOptionInput {
  name: string;
  price: number;
}

export type TPriceAdjustMode = 'percent' | 'amount';

export type TDraftOp =
  | {
      op: 'addCategory';
      tempId: string;
      name: string;
      afterCategoryId?: string | null;
    }
  | { op: 'updateCategory'; categoryId: string; name: string }
  /** afterCategoryId null = first. */
  | { op: 'moveCategory'; categoryId: string; afterCategoryId: string | null }
  /** Deletes its dishes too. */
  | { op: 'deleteCategory'; categoryId: string }
  | {
      op: 'addItem';
      tempId: string;
      categoryId: string;
      afterItemId?: string | null;
      name: string;
      description?: string | null;
      price?: number | null;
      generated?: boolean;
    }
  | {
      op: 'updateItem';
      itemId: string;
      name?: string;
      description?: string | null;
      price?: number | null;
      hidden?: boolean;
      allergens?: number;
      photo?: string | null;
      generated?: boolean;
    }
  | {
      op: 'moveItem';
      itemId: string;
      categoryId: string;
      afterItemId?: string | null;
    }
  | { op: 'deleteItem'; itemId: string }
  | {
      op: 'setSizes';
      itemId: string;
      sizesTitle?: string | null;
      sizes: IDraftSizeInput[];
    }
  | {
      op: 'upsertAddonGroup';
      groupId?: string;
      tempId?: string;
      title: string;
      kind: TAddonGroupKind;
      required: boolean;
      multiple: boolean;
      min: number | null;
      max: number | null;
      options: IDraftAddonOptionInput[];
    }
  | { op: 'deleteAddonGroup'; groupId: string }
  | { op: 'linkAddonGroup'; groupId: string; itemIds: string[] }
  | { op: 'unlinkAddonGroup'; groupId: string; itemIds: string[] }
  | {
      op: 'adjustPrices';
      itemIds: string[] | null;
      categoryIds: string[] | null;
      mode: TPriceAdjustMode;
      value: number;
      roundTo: number | null;
      includeSizes: boolean;
      includeAddons: boolean;
    }
  | { op: 'confirmReview'; itemIds: string[] };

export type TDraftOpName = TDraftOp['op'];

// ---------------------------------------------------------------------------
// Snapshot, deltas, events (§5.4)
// ---------------------------------------------------------------------------

export type TMenuImportStatus =
  | 'collecting'
  | 'reading'
  | 'review'
  | 'published';
export type TMenuImportKind = 'read' | 'copy';

export interface IDraftDeltaRemoved {
  categories?: string[];
  items?: string[];
  addonGroups?: string[];
  questions?: string[];
}

export interface IDraftDeltaMeta {
  languageId: string | null;
  currency: string | null;
}

export interface IDraftDelta {
  /** draft_rev after this mutation. */
  rev: number;
  /** D19 rule evaluated right after this mutation. */
  canUndo: boolean;
  status: TMenuImportStatus;
  categories?: IDraftCategory[];
  items?: IDraftItem[];
  addonGroups?: IDraftAddonGroup[];
  questions?: TDraftQuestion[];
  removed?: IDraftDeltaRemoved;
  categoryOrder?: string[];
  questionOrder?: string[];
  meta?: IDraftDeltaMeta;
  /** Update sessions: the whole live block when its categories or others changed; null when removed. */
  live?: IDraftLive | null;
  /** When only the others to hide changed. */
  liveHiddenIds?: number[];
}

export type TMenuImportPageKind = 'image' | 'text';
export type TMenuImportPageOrigin =
  | 'camera'
  | 'gallery'
  | 'pdf'
  | 'sheet'
  | 'paste';
export type TMenuImportPageWarning = 'blurry' | 'dark' | 'small';
export type TMenuImportPageStatus =
  | 'uploaded'
  | 'queued'
  | 'reading'
  | 'done'
  | 'failed';

export interface IMenuImportPage {
  pageId: string;
  clientPageId: string;
  position: number;
  kind: TMenuImportPageKind;
  origin: TMenuImportPageOrigin;
  originName: string | null;
  originPage: number | null;
  /** 1-based part of a very tall page read in parts; null for a whole page. */
  originPart: number | null;
  storageKey: string | null;
  width: number | null;
  height: number | null;
  clientWarning: TMenuImportPageWarning | null;
  readNo: number | null;
  status: TMenuImportPageStatus;
  errorCode: string | null;
  itemsFound: number;
}

export type TMenuImportReadStatus =
  | 'running'
  | 'done'
  | 'partial'
  | 'failed'
  | 'cancelled';
export type TMenuImportReadStage = 'reading' | 'organizing';

export interface IMenuImportRead {
  readNo: number;
  status: TMenuImportReadStatus;
  stage: TMenuImportReadStage | null;
  reason: string | null;
  pagesTotal: number;
  pagesDone: number;
  itemsFound: number;
  startedAt: string;
  finishedAt: string | null;
}

export type TDraftEffectEntity = 'item' | 'category' | 'addonGroup';
export type TDraftEffectChange = 'add' | 'update' | 'remove';
export type TDraftEffectRecord = IDraftItem | IDraftCategory | IDraftAddonGroup;

export interface IDraftEffect {
  entity: TDraftEffectEntity;
  id: string;
  change: TDraftEffectChange;
  before: TDraftEffectRecord | null;
  after: TDraftEffectRecord | null;
}

export type TMenuProposalStatus = 'pending' | 'applied' | 'discarded' | 'stale';

export interface IMenuProposal {
  proposalId: string;
  summary: string;
  status: TMenuProposalStatus;
  effects: IDraftEffect[];
}

export type TMenuImportTurnStatus = 'thinking' | 'done' | 'failed';

export interface IMenuImportTurn {
  turnId: string;
  clientTurnId: string;
  status: TMenuImportTurnStatus;
  message: string;
  selection: string[];
  reply: string | null;
  proposals: IMenuProposal[];
  errorCode: string | null;
  createdAt: string;
  /** Proposals left out because their changes were too big to show (older APIs omit it). */
  droppedProposals?: number;
}

export interface IMenuImportLimits {
  photosPerRead: 12;
  pagesPerRead: 20;
  textChars: 20000;
  itemsPerDraft: 1500;
}

export interface IMenuImportSnapshot {
  sessionId: string;
  restaurantId: number;
  kind: TMenuImportKind;
  status: TMenuImportStatus;
  rev: number;
  /** null only when the request's sinceRev equals rev (nothing changed in the draft). */
  draft: IMenuDraft | null;
  canUndo: boolean;
  aiEnabled: boolean;
  /** Not removed, by position. */
  pages: IMenuImportPage[];
  /** Latest read. */
  read: IMenuImportRead | null;
  /** Last 30, oldest first, ops stripped. */
  turns: IMenuImportTurn[];
  /** Visible priced dishes of the store (publish dialog). */
  existingItems: number;
  /** Copy sessions. */
  sourceRestaurantName: string | null;
  limits: IMenuImportLimits;
}

export interface IPublishCounts {
  categories: number;
  items: number;
  visibleItems: number;
  hiddenItems: number;
}

/** What a publish of an update session changed on the live menu. */
export interface ISyncCounts {
  priceChanges: number;
  added: number;
  hidden: number;
  shown: number;
}

export interface IPublishResponse {
  /** `sync`: an update session updated the live menu in place. */
  mode: 'append' | 'sync';
  /** Update sessions only. */
  sync: ISyncCounts | null;
  firstMenuPublish: boolean;
  /**
   * The session was already published (another device or tab, or a retry
   * whose first answer was lost): this is the stored result, with
   * `firstMenuPublish: false`. Nothing is reported again for it.
   */
  replayed?: true;
  counts: IPublishCounts;
  restaurant: {
    currency: string;
    languageId: string;
    languageIds: string[] | null;
  };
}

export type TMenuImportEvent =
  | { type: 'delta'; sessionId: string; delta: IDraftDelta }
  | { type: 'resync'; sessionId: string; rev: number }
  | { type: 'page'; sessionId: string; page: IMenuImportPage }
  | { type: 'read'; sessionId: string; read: IMenuImportRead }
  | { type: 'turn'; sessionId: string; turn: IMenuImportTurn };

// ---------------------------------------------------------------------------
// Request and response bodies of `/menu-imports` (§8.3)
// ---------------------------------------------------------------------------

/** `POST /menu-imports` */
export interface ICreateMenuImportDto {
  restaurantId: number;
  uiLocale: string;
}

/** `POST /menu-imports/copy` */
export interface ICopyMenuImportDto {
  restaurantId: number;
  sourceRestaurantId: number;
  uiLocale: string;
}

/** Multipart fields of `POST /menu-imports/:sid/pages` (sent next to the `file` part). */
export interface IMenuImportPageFields {
  clientPageId: string;
  origin: 'camera' | 'gallery' | 'pdf';
  originName?: string | null;
  originPage?: number | null;
  originPart?: number | null;
  clientWarning?: TMenuImportPageWarning | null;
}

/** `POST /menu-imports/:sid/text` */
export interface IMenuImportTextDto {
  clientPageId: string;
  origin: 'paste' | 'sheet';
  originName?: string | null;
  text: string;
}

/** `POST /menu-imports/:sid/reads` */
export interface IStartReadDto {
  note?: string | null;
  retryReadNo?: number;
}

/** `PATCH /menu-imports/:sid/draft` */
export interface IDraftPatchDto {
  ops: TDraftOp[];
  /** UUID v4, one per queued mutation and reused on each of its retries (§8.5 idempotent retries). */
  clientMutationId?: string;
}

/** `PATCH /menu-imports/:sid/draft` → 200 */
export interface IDraftPatchResult {
  delta: IDraftDelta;
  /** tempId → the id the server gave the new record. */
  tempIds: Record<string, string>;
  /**
   * An earlier attempt of this `clientMutationId` was already applied (its
   * answer was lost): nothing was applied again, `tempIds` are the stored
   * ones and `delta` carries only the current `rev`, `canUndo` and `status`.
   */
  replayed?: true;
}

/** 200 body of answer, apply and undo. */
export interface IDraftDeltaResult {
  delta: IDraftDelta;
  /**
   * An earlier attempt of this `clientMutationId` was already applied (its
   * answer was lost): nothing was applied again and `delta` carries only the
   * current `rev`, `canUndo` and `status`.
   */
  replayed?: true;
}

/** `POST /menu-imports/:sid/questions/:questionId/answer` */
export interface IAnswerQuestionDto {
  answer: TQuestionAnswer;
  itemIds?: string[];
  categoryName?: string;
  /** UUID v4, one per queued answer and reused on each of its retries. */
  clientMutationId?: string;
}

/** `POST /menu-imports/:sid/turns/:turnId/proposals/:proposalId/apply` */
export interface IApplyProposalDto {
  /** UUID v4, one per queued apply and reused on each of its retries. */
  clientMutationId?: string;
}

export interface IRetryOfDto {
  turnId: string;
  proposalId: string;
}

/** `POST /menu-imports/:sid/turns` */
export interface ISendTurnDto {
  clientTurnId: string;
  message: string;
  selection?: string[];
  retryOf?: IRetryOfDto;
}

/** `POST /menu-imports/:sid/undo` */
export interface IUndoDto {
  rev: number;
  /** UUID v4, one per queued undo and reused on each of its retries. */
  clientMutationId?: string;
}

/** `POST /menu-imports/:sid/publish` (the server decides how: an update session updates in place). */
export interface IPublishDto {
  rev: number;
}

/** `POST /menu-imports/:sid/live`: a refresh (empty) or one owner decision. */
export interface ILiveBody {
  /** UUID v4, one per queued decision and reused on each of its retries. */
  clientMutationId?: string;
  link?: { itemId: string; liveItemId: number | null };
  hide?: { liveItemIds: number[]; hidden: boolean };
}

/** `POST /menu-imports/:sid/undo-update` → 200 */
export interface IUndoUpdateResponse {
  /** Rows put back as they were. */
  reverted: number;
  /** Rows kept because they changed after the update. */
  kept: number;
}
